import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Eye, Save, Send, RotateCcw } from "lucide-react";

const FIELD_LABELS = { subject: "Asunto", title: "Título", body: "Texto", button: "Botón", button2: "Segundo botón" };
const varsIn = (s) => (s || "").match(/\{[^{}]+\}/g) || [];

function VarText({ text }) {
  const parts = (text || "").split(/(\{[^{}]+\})/g);
  return parts.map((p, i) => (/^\{[^{}]+\}$/.test(p)
    ? <span key={i} className="px-1 rounded bg-[#FEF3C7] border border-[#F59E0B] text-[#78350F] font-medium">{p}</span>
    : <span key={i}>{p}</span>));
}

function FieldEditor({ name, value, original, onChange, onFocusRef }) {
  const missing = varsIn(original).filter((v) => !(value || "").includes(v));
  const multi = name === "body";
  const Tag = multi ? "textarea" : "input";
  return (
    <div className="space-y-1" data-testid={`email-field-${name}`}>
      <div className="label-eyebrow">{FIELD_LABELS[name]}</div>
      <Tag
        className={`field text-sm w-full ${multi ? "min-h-[260px] font-mono leading-relaxed" : ""}`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={(e) => { onFocusRef.current = { name, el: e.target }; }}
        data-testid={`email-input-${name}`}
      />
      <div className="text-xs text-[#6B6B6B] bg-[#FBF7EC] rounded-lg p-2 whitespace-pre-line"><VarText text={value} /></div>
      {missing.length > 0 && (
        <div className="text-xs text-red-700 flex flex-wrap items-center gap-2" data-testid={`email-missing-${name}`}>
          Falta: {missing.map((v) => (
            <button key={v} type="button" className="underline" onClick={() => onChange(`${value || ""} ${v}`)}>volver a poner {v}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EmailTextsPanel() {
  const [items, setItems] = useState(null);
  const [selKey, setSelKey] = useState(null);
  const [draft, setDraft] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const focusRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/admin/email-texts");
    setItems(data);
    return data;
  };
  useEffect(() => { load().catch(() => toast.error("No pude cargar los textos de los emails")); }, []);

  const sel = items?.find((i) => i.key === selKey);
  const select = (it) => { setSelKey(it.key); setDraft({ ...it.fields }); setPreview(null); };
  const originals = sel ? sel.fields : {};
  const anyMissing = sel && Object.keys(draft).some((f) => varsIn(sel.defaults?.[f]).some((v) => !(draft[f] || "").includes(v)));

  const insertVar = (v) => {
    const f = focusRef.current;
    if (!f || !(f.name in draft)) { setDraft((d) => ({ ...d, body: `${d.body || ""}${v}` })); return; }
    const pos = f.el.selectionStart ?? (draft[f.name] || "").length;
    const cur = draft[f.name] || "";
    setDraft((d) => ({ ...d, [f.name]: cur.slice(0, pos) + v + cur.slice(pos) }));
  };

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo completar"); }
    setBusy(false);
  };

  if (!items) return <div className="card text-sm text-[#6B6B6B]">Cargando textos…</div>;
  return (
    <div className="grid md:grid-cols-[280px_1fr] gap-6" data-testid="email-texts-panel">
      <div className="card p-0 overflow-hidden">
        {items.map((it) => (
          <button key={it.key} onClick={() => select(it)} data-testid={`email-text-item-${it.key}`}
            className={`block w-full text-left px-4 py-3 border-b border-[#B8912E]/10 text-sm transition-colors ${selKey === it.key ? "bg-[#B8912E]/15" : "hover:bg-[#FBF7EC]"}`}>
            <div className="font-medium">{it.label}</div>
            {it.customized && <div className="text-[10px] text-[#B8912E] mt-0.5">Personalizado</div>}
          </button>
        ))}
      </div>
      {!sel ? <div className="card text-sm text-[#6B6B6B]">Elige un email de la lista para editar su asunto y su texto.</div> : (
        <div className="card space-y-4">
          <div className="font-serif text-2xl gold">{sel.label}</div>
          <div className="text-xs text-[#6B6B6B]">
            Las variables entre llaves se sustituyen al enviar. No se puede guardar si falta alguna. Pulsa una para insertarla donde tengas el cursor.
            Usa una línea vacía para separar párrafos, **texto** para negrita y «## Título» para un subtítulo.
          </div>
          <div className="flex flex-wrap gap-2" data-testid="email-var-chips">
            {sel.vars.map((v) => (
              <button key={v} type="button" onClick={() => insertVar(`{${v}}`)} data-testid={`email-var-${v}`}
                className="px-2 py-0.5 rounded-full text-xs bg-[#FEF3C7] border border-[#F59E0B] text-[#78350F]">{`{${v}}`}</button>
            ))}
          </div>
          {Object.keys(draft).map((f) => (
            <FieldEditor key={f} name={f} value={draft[f]} original={sel.defaults?.[f] ?? originals[f]} onChange={(v) => setDraft((d) => ({ ...d, [f]: v }))} onFocusRef={focusRef} />
          ))}
          <div className="flex flex-wrap gap-2">
            <button className="btn-ghost text-sm inline-flex items-center gap-1" disabled={busy} data-testid="email-preview-btn"
              onClick={() => run(async () => { const { data } = await api.post(`/admin/email-texts/${sel.key}/preview`, draft); setPreview(data); })}>
              <Eye size={14}/> Vista previa
            </button>
            <button className="btn-ghost text-sm inline-flex items-center gap-1" disabled={busy || anyMissing} data-testid="email-test-btn"
              onClick={() => run(() => api.post(`/admin/email-texts/${sel.key}/test`, draft), "Prueba enviada a miconsultaconbea@gmail.com")}>
              <Send size={14}/> Enviarme prueba
            </button>
            <button className="btn-primary text-sm inline-flex items-center gap-1" disabled={busy || anyMissing} data-testid="email-save-btn"
              onClick={() => run(async () => { await api.put(`/admin/email-texts/${sel.key}`, draft); const d = await load(); setSelKey(sel.key); setDraft({ ...d.find((i) => i.key === sel.key).fields }); }, "Guardado. Se usará en los próximos emails.")}>
              <Save size={14}/> Guardar
            </button>
            {sel.customized && (
              <button className="btn-ghost text-sm inline-flex items-center gap-1 text-red-700" disabled={busy} data-testid="email-reset-btn"
                onClick={() => { if (window.confirm("¿Volver al texto original de este email?")) run(async () => { await api.delete(`/admin/email-texts/${sel.key}`); const d = await load(); select(d.find((i) => i.key === sel.key)); }, "Texto original restaurado"); }}>
                <RotateCcw size={14}/> Restaurar original
              </button>
            )}
          </div>
          {preview && (
            <div className="space-y-2" data-testid="email-preview">
              <div className="text-sm"><span className="label-eyebrow mr-2">Asunto</span>{preview.subject}</div>
              <iframe title="Vista previa" srcDoc={preview.html} className="w-full h-[520px] rounded-xl border border-[#B8912E]/20 bg-white" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
